import { Component, OnInit, inject, viewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CampaignsCampaignAdminApiClient,
  CampaignsReceiptAdminApiClient,
  LineItem,
  Receipt,
  ReceiptHistoryChange,
  UpdateReceiptRequest
} from 'src/app/api/api-campaigns.service.generated';
import { environment } from 'src/environments/environment';
import { ReceiptLinesComponent } from '../receipt-lines/receipt-lines.component';
import { FormArray, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RECEIPT_TAGS } from '../../const/receipt-tag';
import { MatDialog } from '@angular/material/dialog';
import { ReceiptSavingConfirmationComponent } from 'src/app/receipts/receipt-details/receipt-saving-confirmation/receipt-saving-confirmation.component';
import { PERMISSION } from 'src/app/const/permission';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreadcrumbsComponent, ButtonComponent, ColumnData, NgxDataTableComponent, PageContainerComponent, PhotoComponent } from 'wecheer-common';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { format } from 'date-fns';
import { MatIconModule } from '@angular/material/icon';
import { lastValueFrom } from 'rxjs';


@UntilDestroy()
@Component({
  selector: 'app-receipt-details',
  templateUrl: './receipt-details.component.html',
  styleUrls: ['./receipt-details.component.scss'],
  imports: [
    ReceiptLinesComponent,
    PhotoComponent,
    MatCardModule,
    MatTabsModule,
    MatFormFieldModule,
    MatTimepickerModule,
    MatTableModule,
    NgxMatTimepickerModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    ReactiveFormsModule,
    HasAnyPermissionDirective,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent,
    NgxDataTableComponent
  ]
})
export class ReceiptDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private snackbar = inject(MatSnackBar);
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);
  private receiptApi = inject(CampaignsReceiptAdminApiClient);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);

  readonly picker = viewChild<any>('picker');
  readonly receiptLines = viewChild<ReceiptLinesComponent>('receiptLines');

  receipt: Receipt;
  receiptId: string;
  isLoading: boolean;
  raw: any = {
    line_items: [],
    date: null,
    invoice_number: "",
    vendor: {
      name: ""
    }
  };
  time: any;

  // Form Controls
  scanDateControl = new FormControl({ value: null, disabled: true });
  userPhoneControl = new FormControl({ value: '', disabled: true });
  verifyDocIdControl = new FormControl({ value: '', disabled: true });
  invoiceDateControl = new FormControl();
  timeControl = new FormControl();
  invoiceNumberControl = new FormControl();
  referenceNumberControl = new FormControl({ value: '', disabled: true });
  vendorNameControl = new FormControl();
  campaignNameControl = new FormControl({ value: '', disabled: true });
  campaignIdControl = new FormControl({ value: '', disabled: true });
  stageControl = new FormControl({ value: '', disabled: true });
  suspectGroupControl = new FormControl({ value: '', disabled: true });


  receiptChanges: ReceiptHistoryChange[];
  columns: ColumnData[] = [
    { key: 'date', label: 'Date' },
    { key: 'change', label: 'Change', colWidth: 50 },
    { key: 'field', label: 'Field' },
    { key: 'from', label: 'From' },
    { key: 'to', label: 'To' },
    { key: 'changedBy', label: 'Changed By' }
  ];
  saveDisabled: boolean = true;
  isTagChanged = false;
  isDetailsChanged = false;
  saveText = 'Save';
  readonly permissions = PERMISSION;

  constructor() {
    this.route.params.subscribe(() => {
      this.receiptId = this.route.snapshot.params.id;
      this.bindReceipt();
    });
  }

  async ngOnInit() {
    this.receiptChanges = await this.getReceiptChanges();
    this.refreshPushToRepocessingStatus(this.receipt?.tags);
  }

  async bindReceipt() {
    this.receipt = await lastValueFrom(this.receiptApi
      .getReceiptById(this.receiptId));

    // Populate form controls with receipt data
    this.scanDateControl.setValue(format(this.receipt.createDate, 'MMM dd, yyyy, hh:mm:ss a'));
    this.userPhoneControl.setValue(this.receipt.userPhoneNo);
    this.verifyDocIdControl.setValue(this.receipt.docId ?? this.receipt.id);
    this.invoiceDateControl.setValue(this.receipt.invoiceDate);
    this.invoiceNumberControl.setValue(this.receipt.invoiceNumber);
    this.referenceNumberControl.setValue(this.receipt.referenceNumber);
    this.vendorNameControl.setValue(this.receipt.vendor?.name);
    this.campaignNameControl.setValue(this.receipt.campaignName);
    this.campaignIdControl.setValue(this.receipt.campaignId);
    this.stageControl.setValue(this.getStage());
    this.suspectGroupControl.setValue(this.receipt.suspectDuplicateGroup || 'n/a');

    if (this.receipt.invoiceDate) {
      this.time = this.getTimeFromIsoString(this.receipt.invoiceDate);
      this.timeControl.setValue(this.time);
    }
    if (this.receipt.rawJson) {
      this.raw = JSON.parse(this.receipt.rawJson);
    }
    this.refreshPushToRepocessingStatus(this.receipt?.tags);
  }

  onImageClick() {
    window.open(this.receipt.receiptUrl, '_blank');
  }

  async onSeeCampaign() {
    const campaign = await lastValueFrom(this.campaignsClient
      .getCampaign(this.receipt.campaignId));
    if (!campaign) {
      this.snackbar.open('Campaign not found', 'Okay', { duration: 3000 });
      return;
    }

    let url;
    const isMultiCampaign =
      !campaign.isPunchCardStrategy &&
      !campaign.isLuckyDrawStrategy &&
      !campaign.isStaffCampaignStrategy &&
      !campaign.isReferralCampaignStrategy;

    if (campaign.isStaffCampaignStrategy)
      url = `${environment.baseUrl}/staff-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isReferralCampaignStrategy)
      url = `${environment.baseUrl}/referral-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isPunchCardStrategy)
      url = `${environment.baseUrl}/punch-card-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isLuckyDrawStrategy)
      url = `${environment.baseUrl}/lucky-draw-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (isMultiCampaign)
      url = `${environment.baseUrl}/multi-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;

    // open in new window
    if (url) {
      window.open(url, '_blank');
      return;
    }
  }

  unique_id() {
    return 'xxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  onSuspectDuplicateGroupClick() {
    const queryParams = {
      suspectDuplicateGroup: this.receipt.suspectDuplicateGroup,
    };

    this.router.navigate(['/receipts'], {
      queryParams: {
        ...queryParams,
        v: this.unique_id(), // add random param value to create unique query string - to trigger queryParams change even if no param has changed
      },
    });
  }

  markAllAsTouched(formGroup: FormGroup | FormArray) {
    (Object as any).values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.controls) {
        this.markAllAsTouched(control);
      }
    });
  }

  addTimeToDate(date, timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);

    const newDate = new Date(date);

    // Get the local time zone offset in minutes
    const timezoneOffset = date.getTimezoneOffset();

    // Adjust the time to make sure it not effected when stringify it, by subtracting the local timezone offset
    const adjustedHours = hours - Math.floor(timezoneOffset / 60);
    const adjustedMinutes = minutes - (timezoneOffset % 60);

    newDate.setHours(adjustedHours);
    newDate.setMinutes(adjustedMinutes);

    return newDate;
  }

  getTimeFromIsoString(date) {
    // Convert date to ISO string in UTC format
    const isoString = date.toISOString();

    // Extract time components from ISO string
    const parts = isoString.match(/\d+/g);
    if (!parts || parts.length < 6) {
      return 'Invalid date';
    }

    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // Months are 0-based
    const day = parseInt(parts[2]);
    const hour = parseInt(parts[3]);
    const minute = parseInt(parts[4]);
    const second = parseInt(parts[5]);

    const utcDate = new Date(Date.UTC(year, month, day, hour, minute, second));

    const hours = utcDate.getUTCHours().toString().padStart(2, '0');
    const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');

    const formattedTime = `${hours}:${minutes}`;
    return formattedTime;
  }

  getStage() {
    if (!this.receipt.tags) {
      return '';
    }
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.NOT_ENOUGH_STAMPS) !== -1)
      return 'Not enough stamp';
    if (
      this.receipt.tags.indexOf(RECEIPT_TAGS.STAMPS_RECEIVED_TAG) !== -1
      ||
      this.receipt.tags.indexOf(RECEIPT_TAGS.POINTS_RECEIVED_TAG) !== -1
    )
      return 'Eligible';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.NOT_RECOGNIZABLE_TAG) !== -1)
      return 'Not recognizable';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.VERIFY_DUPLICATE_TAG) !== -1)
      return 'Veryfi duplication';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.SUSPECT_DUPLICATE_TAG) !== -1)
      return 'Suspect duplicated';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.SUSPECT_LOCATION_TAG) !== -1)
      return 'Suspect location';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.TIME_CONSTRAINT_NOT_VALID) !== -1)
      return 'Time constraint not valid';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.NOT_ELIGIBLE_TAG) !== -1) return 'Not eligible';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.INCOMPLETE_TAG) !== -1) return 'Incomplete';
    if (this.receipt.tags.indexOf(RECEIPT_TAGS.MANUAL_VERIFY_TAG) !== -1) return 'Forced manual review';
    return 'unknown';
  }

  async submit() {
    const receiptLines = this.receiptLines();
    this.markAllAsTouched(receiptLines.mainForm);
    receiptLines.setButtonDisable(true);
    var note = receiptLines.mainForm.controls['note'].value;
    var numberOfStamps = receiptLines.mainForm.controls['numberOfStamps'].value;
    var tags = receiptLines.tags;

    if (!receiptLines.mainForm.valid) {
      const confirmLabel = this.saveText;
      const confirmMessage = `Line item details is missing. Are you sure you want to ${confirmLabel.toLowerCase()} this receipt?`;
      const confirmed = await lastValueFrom(
        this.dialog.open(ReceiptSavingConfirmationComponent, {
          data: {
            confirmLabel,
            message: confirmMessage
          }
        }).afterClosed()
      );
      if (confirmed) {
        this.SaveData(tags, note, numberOfStamps, true, false, null);
      } else {
        receiptLines.setButtonDisable(false);
      }
      return;
    }

    // Update receipt with FormControl values
    this.receipt.invoiceDate = this.invoiceDateControl.value;
    this.receipt.invoiceNumber = this.invoiceNumberControl.value;
    if (this.receipt.vendor) {
      this.receipt.vendor.name = this.vendorNameControl.value;
    }

    if (this.timeControl.value) {
      this.receipt.invoiceDate = this.addTimeToDate(
        this.receipt.invoiceDate,
        this.timeControl.value
      );
    }

    const lineItems = receiptLines.mainForm.get('lineItems') as FormArray;

    this.raw.line_items = lineItems?.value;
    this.raw.date = this.receipt?.invoiceDate;
    this.raw.invoice_number = this.receipt?.invoiceNumber;
    this.raw.vendor.name = this.receipt?.vendor?.name;
    this.receipt.rawJson = this.raw && JSON.stringify(this.raw);

    const lineItemData = lineItems.value?.map(
      (lineItem) => new LineItem({
        name: lineItem.description,
        amount: lineItem.quantity ?? 0,
        price: lineItem.price ?? 0,
        discount: lineItem.discount ?? 0,
        total: lineItem.total ?? 0
      })
    );
    this.receipt.lineItems = lineItemData;
    this.SaveData(tags, note, numberOfStamps, this.isTagChanged, this.isDetailsChanged, this.receipt);
  }

  private SaveData(tags: string[],
    note: string,
    numberOfStamps: any,
    isReprocess: boolean,
    isUpdateReceipt: boolean,
    receipt: Receipt) {
    var request = new UpdateReceiptRequest({
      actions: tags,
      note: note,
      numberOfStamps: numberOfStamps,
      receipt: receipt,
      isReprocess: isReprocess,
      isUpdateReceipt: isUpdateReceipt
    });
    this.receiptApi
      .updateReceiptById(this.receipt.id, request)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (isReprocess == false && !this.receipt?.tags?.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
          this.receiptLines().setButtonDisable(false);
        }
        this.isTagChanged = false;
        this.isDetailsChanged = false;
        this.saveText = "Save";
        this.snackbar.open('Update receipt successfully!', null, { duration: 3000 });
        this.bindReceipt();
      });
  }

  async getReceiptChanges() {
    return await lastValueFrom(this.receiptApi
      .getReceiptHistory(this.receiptId));
  }

  refreshPushToRepocessingStatus(tags: string[]) {
    if ((tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG)
      || tags?.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD)
      || tags?.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG))
      || tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG))) {
      this.saveDisabled = false;
    }
    if (tags?.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
      this.saveDisabled = true;
    }
    if (this.isDetailsChanged || this.isTagChanged) {
      this.saveDisabled = false;
    }
  }

  tagsChangedHandler($event) {
    if ($event == true) {
      this.isTagChanged = true;
      this.saveText = 'Save and Reprocess';
      this.refreshPushToRepocessingStatus(this.receiptLines()?.tags);
    }
  }

  detailsChangedHandler() {
    this.isDetailsChanged = true;
    this.refreshPushToRepocessingStatus(this.receiptLines()?.tags);
  }
}
